import { Axios } from 'axios';
import {
  BucketCreateDto,
  BucketUpdateAclDto,
  BucketUpdateObjectLockingDto, BucketUpdateVersioningDto,
  IBucketAclDto,
  IBucketByNameDto,
  IBucketListDto, IBucketObjectLockingDto, IBucketVersioningDto,
  ObjectsMoveDto,
} from 'components/storage/types/bucket';
import { BuckerCorsRulesDto, IBuckerCorsRulesRes } from 'components/storage/types/bucket-cors';
import {
  IStorageObjectAclDto, IStorageObjectMetaDto,
  IStorageObjectsDeleteDto,
  IStorageObjectShareLinkDto,
  IStorageObjectsListDto, IStorageObjectUpdateAclDto,
  IStorageObjectUrlDto,
  IStorageObjectVersionsDto, ObjectAccess,
} from 'components/storage/types/object';
import apiClient from 'services/api/axios';
import { encodeObjectKey } from 'utils/formatters';

// export function getObjectUrl(bucketKey: string, objectKey: string, versionId?: string): string {
//   if (process.env.REACT_APP_MODE === 'DEV') {
//     return `https://${bucketKey}.s3-storage.uz/${encodeObjectKey(objectKey)}${versionId ? `?versionId=${versionId}` : ''}`;
//   }
//   return `https://${bucketKey}.${getDynamicTranslation('storage.storage_url')}/${encodeObjectKey(objectKey)}`;
// }

class StorageService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  getBuckets(pageSize: number, nextPageToken: string | null) {
    return this.api.get<IBucketListDto>('v1/buckets', {
      baseURL: '/panel-main/storage/api',
      params: {
        maxBuckets: pageSize,
        continuationToken: nextPageToken || undefined,
      },
    });
  }

  getBucketByName(bucketKey: string) {
    return this.api.get<IBucketByNameDto>(`v1/buckets/${bucketKey}`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  createBucket(dto: BucketCreateDto) {
    return this.api.post('v1/buckets', dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  deleteBucket(bucketKey: string) {
    return this.api.delete(`v1/buckets/${bucketKey}`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getBucketVersioning(bucketKey: string) {
    return this.api.get<IBucketVersioningDto>(`v1/buckets/${bucketKey}/versioning`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  updateBucketVersioning(bucketKey: string, dto: BucketUpdateVersioningDto) {
    return this.api.put(`v1/buckets/${bucketKey}/versioning`, dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getBucketObjectLocking(bucketKey: string) {
    return this.api.get<IBucketObjectLockingDto>(`v1/buckets/${bucketKey}/object-locking`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  updateBucketObjectLocking(bucketKey: string, dto: BucketUpdateObjectLockingDto) {
    return this.api.put(`v1/buckets/${bucketKey}/object-locking`, dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getBucketAcl(bucketKey: string) {
    return this.api.get<IBucketAclDto>(`v1/buckets/${bucketKey}/acl`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  updateBucketAcl(bucketKey: string, dto: BucketUpdateAclDto) {
    return this.api.put(`v1/buckets/${bucketKey}/acl`, dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getObjects(bucketKey: string, pageSize: number, nextPageToken: string | null, prefix?: string) {
    return this.api.get<IStorageObjectsListDto>(`v1/buckets/${bucketKey}/objects`, {
      baseURL: '/panel-main/storage/api',
      params: {
        maxKeys: pageSize,
        continuationToken: nextPageToken || undefined,
        prefix,
      },
    });
  }

  uploadFile(bucketKey: string, acl: ObjectAccess, dto: FormData) {
    return this.api.post<IStorageObjectsListDto>(`v1/buckets/${bucketKey}/objects`, dto, {
      baseURL: '/panel-main/storage/api',
      params: {
        acl,
      },
      timeout: 1000 * 60 * 60, // 1 hour
    });
  }

  getObjectMeta(bucketKey: string, objectKey: string, objectVersionId?: string) {
    return this.api.get<IStorageObjectMetaDto>(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/head`, {
      baseURL: '/panel-main/storage/api',
      params: {
        objectVersionId,
      },
    });
  }

  getObjectAcl(bucketKey: string, objectKey: string) {
    return this.api.get<IStorageObjectAclDto>(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/acl`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  updateObjectAcl(bucketKey: string, objectKey: string, dto: IStorageObjectUpdateAclDto) {
    return this.api.patch(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/acl`, dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getObjectVersions(bucketKey: string, objectKey: string) {
    return this.api.get<IStorageObjectVersionsDto>(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/versions`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getObjectShareLink(
    bucketKey: string,
    objectKey: string,
    duration: number,
    versionId: string | null,
  ) {
    return this.api.get<IStorageObjectShareLinkDto>(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/presigned-url`, {
      baseURL: '/panel-main/storage/api',
      params: {
        duration,
        versionId: versionId || undefined,
      },
    });
  }

  deleteObjects(bucketKey: string, dto: IStorageObjectsDeleteDto) {
    return this.api.delete(`v1/buckets/${bucketKey}/objects/batch`, {
      data: dto,
      baseURL: '/panel-main/storage/api',
    });
  }

  deleteObject(bucketKey: string, objectKey: string) {
    return this.api.delete(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}`, {
      baseURL: '/panel-main/storage/api',
      params: {
        deleteAllVersions: true,
      },
    });
  }

  deleteObjectVersion(bucketKey: string, objectKey: string, versionId: string) {
    return this.api.delete(`v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}/versions/${versionId}`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  deleteAllObjectsInBucket(bucketKey: string) {
    return this.api.delete(`v1/buckets/${bucketKey}/objects`, {
      baseURL: '/panel-main/storage/api',
    });
  }

  objectsMove(bucketKey: string, dto: ObjectsMoveDto) {
    return this.api.post(`v1/buckets/${bucketKey}/objects/move`, dto, {
      baseURL: '/panel-main/storage/api',
    });
  }

  getObjectUrl(bucketKey: string, objectKey: string) {
    return this.api.get<IStorageObjectUrlDto>(
      `/v1/buckets/${bucketKey}/objects/${encodeObjectKey(objectKey)}`,
      {
        baseURL: '/panel-main/storage/api',
      },
    );
  }

  getBucketCorsRules(bucketKey: string) {
    return this.api.get<IBuckerCorsRulesRes>(
      `/v1/buckets/${bucketKey}/cors`,
      {
        baseURL: '/panel-main/storage/api',
      },
    );
  }

  updateBucketCorsRules(bucketKey: string, dto: BuckerCorsRulesDto) {
    return this.api.post(
      `/v1/buckets/${bucketKey}/cors`,
      dto,
      {
        baseURL: '/panel-main/storage/api',
      },
    );
  }

  deleteAllCorsRules(bucketKey: string) {
    return this.api.delete(
      `/v1/buckets/${bucketKey}/cors`,
      {
        baseURL: '/panel-main/storage/api',
      },
    );
  }
}

export default new StorageService(apiClient);
